// adult-birthday — b03 Bayramcha. Sophisticated grown-up birthday guest site.
//
// Design notes (different from kid Confetti b02):
//   * Cocktail-party energy, not kid-festive. Bodoni Moda italic, champagne
//     palette, deliberate restraint.
//   * Hero: photo on the right, eyebrow + age callout + names on the left.
//     Ambient champagne bubbles drift up softly behind the hero (signature)
//     — small SVG circles, opacity-fade, 6–12s rise, subtle stagger. Reduced-
//     motion: bubbles render static.
//   * Sparkle dots: ~15 small SVG diamonds scattered, all static (adult
//     restraint — not animated confetti chaos).
//   * UNIQUE section: "Cocktail Menu" — three signature drinks listed in
//     Bodoni Moda italic with rose hairlines.
//   * Sound triggers: RSVP chime + pop (yes), tap (no), pop on cocktail
//     hover, button click. Music plays via the GuestPage music feature.
//   * All sections wrapped to be mobile-responsive. `prefers-reduced-motion`
//     silences bubble rise & sparkle motion.
import { useEffect, useRef, useState } from "react";
import {
  GuestPhoto,
  CountdownSection,
  StorySection,
  GallerySection,
  ScheduleSection,
  MapSection,
  RsvpSection,
  GuestbookSection,
  ShareSection,
  GuestFooter,
} from "@/components/sections";
import * as O from "@/components/Ornaments";
import { useSound } from "@/lib/sound";
import { useT } from "@/lib/i18n";
import { numToWord } from "@/lib/numwords";
import type { VariantProps } from "./types";

// ─── Reduced-motion hook ───────────────────────────────────────────────
function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduce(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);
  return reduce;
}

// ─── Champagne bubble (signature ambient) ──────────────────────────────
interface Bubble {
  id: number;
  left: number; // %
  size: number; // px (5..15)
  delay: number; // s (negative for stagger)
  duration: number; // s (6..12)
  drift: number; // px lateral wander
  opacity: number;
}

function buildBubbles(n: number): Bubble[] {
  // Deterministic rng — stable across mounts so SSR/client agree.
  const rng = (seed: number) => {
    const x = Math.sin(seed * 9301 + 49297) * 233280;
    return x - Math.floor(x);
  };
  return Array.from({ length: n }).map((_, i) => ({
    id: i,
    left: rng(i + 1) * 100,
    size: 5 + rng(i + 100) * 10,
    delay: -rng(i + 200) * 12,
    duration: 6 + rng(i + 300) * 6,
    drift: (rng(i + 400) - 0.5) * 40,
    opacity: 0.35 + rng(i + 500) * 0.4,
  }));
}

// ─── Static sparkle field ──────────────────────────────────────────────
const SPARKLE_POSITIONS: Array<{ left: number; top: number; size: number }> = [
  { left: 6, top: 8, size: 9 },
  { left: 14, top: 28, size: 7 },
  { left: 8, top: 52, size: 8 },
  { left: 22, top: 70, size: 6 },
  { left: 4, top: 84, size: 7 },
  { left: 30, top: 14, size: 6 },
  { left: 38, top: 88, size: 8 },
  { left: 50, top: 6, size: 7 },
  { left: 62, top: 22, size: 8 },
  { left: 70, top: 78, size: 6 },
  { left: 80, top: 12, size: 9 },
  { left: 86, top: 42, size: 7 },
  { left: 92, top: 64, size: 8 },
  { left: 76, top: 90, size: 6 },
  { left: 94, top: 86, size: 7 },
];

// ─── Cocktail spec ─────────────────────────────────────────────────────
// Cocktail names and recipes live in the dictionary.
const COCKTAIL_KEYS: Array<{ nameKey: string; recipeKey: string }> = [
  { nameKey: "variant.b03.cocktails.0.name", recipeKey: "variant.b03.cocktails.0.recipe" },
  { nameKey: "variant.b03.cocktails.1.name", recipeKey: "variant.b03.cocktails.1.recipe" },
  { nameKey: "variant.b03.cocktails.2.name", recipeKey: "variant.b03.cocktails.2.recipe" },
];

// ─── Variant ───────────────────────────────────────────────────────────
export function AdultBirthdayVariant({ t, isDark, ctx }: VariantProps) {
  const tt = useT();
  const { play } = useSound();
  const reduce = usePrefersReducedMotion();
  const photos = t.photos || [];

  // If the user encoded an age in t.names (e.g. "Nodira · 30" or "Nodira 35
  // yosh"), render a numeric "{N} yosh" callout in place of the localized
  // "thirty-year celebration" phrase. When no number is present, keep the
  // localized phrase — that's the design's default ambient mood.
  const ageMatch = t.names.match(/(\d{1,3})\s*(yosh|yil)?\s*$/i);
  const ageNumber = ageMatch ? ageMatch[1] : "";
  const cleanName = t.names.replace(/\s*[·•]?\s*\d+\s*(yosh|yil)?\s*$/i, "").trim() || t.names;

  // Build bubbles once per mount.
  const bubblesRef = useRef<Bubble[]>(buildBubbles(22));
  const bubbles = bubblesRef.current;

  // RSVP wrappers — sound feedback.
  const wrappedSetRsvp = (v: string | null) => {
    if (v === "yes") {
      play("chime");
      window.setTimeout(() => play("pop"), 150);
    } else if (v === "no") {
      play("tap");
    }
    ctx.rsvp.setRsvp(v);
  };

  return (
    <div
      className="b03-adult-birthday"
      style={{
        background: t.bg,
        color: t.ink,
      }}
    >
      <style>{`
        @keyframes b03-bubble-rise {
          0%   { transform: translate3d(0, 0, 0); opacity: 0; }
          12%  { opacity: var(--b03-bub-op, 0.6); }
          88%  { opacity: var(--b03-bub-op, 0.6); }
          100% { transform: translate3d(var(--b03-bub-dx, 0px), -100vh, 0); opacity: 0; }
        }
        @keyframes b03-fade-up {
          0%   { opacity: 0; transform: translate3d(0, 12px, 0); }
          100% { opacity: 1; transform: translate3d(0, 0, 0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .b03-adult-birthday .b03-bubble {
            animation: none !important;
            opacity: var(--b03-bub-op, 0.45) !important;
          }
          .b03-adult-birthday .b03-fade-in { animation: none !important; }
        }

        /* Mobile overrides — sophistication holds, sizes shrink */
        @media (max-width: 768px) {
          .b03-adult-birthday .b03-hero {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
            /* Hero uses padding: "100px 64px 120px" inline. The global
               lockdown targets "80px 60" or "100px 60" but not "100px 64".
               Override explicitly so 64px side padding doesn't clip content
               on a 390px viewport. */
            padding: 64px 18px 48px !important;
            text-align: center !important;
          }
          .b03-adult-birthday .b03-hero-photo {
            order: -1 !important;
            width: 100% !important;
            max-width: none !important;
            height: min(60vh, 560px) !important;
            margin: 0 auto !important;
            aspect-ratio: auto !important;
          }
          .b03-adult-birthday .b03-eyebrow {
            justify-content: center !important;
          }
          .b03-adult-birthday .b03-names {
            font-size: clamp(36px, 12vw, 96px) !important;
          }
          .b03-adult-birthday .b03-age-callout {
            margin: 16px auto 0 !important;
            /* Prevent the age callout inline-flex from overflowing at 390px */
            max-width: calc(100vw - 36px) !important;
            box-sizing: border-box !important;
          }
          .b03-adult-birthday .b03-section {
            padding: 64px 18px !important;
          }
          .b03-adult-birthday .b03-cocktails {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
          }
          .b03-adult-birthday .b03-cocktail {
            padding: 22px 20px !important;
          }
          .b03-adult-birthday .b03-cta {
            width: 100% !important;
            min-height: 52px !important;
          }
          .b03-adult-birthday button,
          .b03-adult-birthday a[role="button"] {
            min-height: 44px;
          }
          /* Date/venue row — let it wrap on very narrow screens */
          .b03-adult-birthday .b03-hero > div:first-of-type > div:last-child {
            flex-wrap: wrap !important;
          }
        }
        .b03-adult-birthday button:focus-visible,
        .b03-adult-birthday a:focus-visible {
          outline: 2px solid ${t.accent};
          outline-offset: 3px;
        }
      `}</style>

      {/* ═══════════════════════════════════════════════════════════════
          HERO — photo on right, names + age callout on left.
          Champagne bubbles drift behind everything. Sparkle field static.
          ═══════════════════════════════════════════════════════════════ */}
      <section
        className="b03-hero"
        style={{
          position: "relative",
          minHeight: "100vh",
          padding: "100px 64px 120px",
          display: "grid",
          gridTemplateColumns: "1.1fr 1fr",
          gap: 72,
          alignItems: "center",
          overflow: "hidden",
        }}
      >
        {/* Bubble layer (signature) */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            zIndex: 0,
            overflow: "hidden",
          }}
        >
          {bubbles.map((b) => (
            <span
              key={b.id}
              className="b03-bubble"
              style={{
                position: "absolute",
                left: `${b.left}%`,
                bottom: -20,
                width: b.size,
                height: b.size,
                borderRadius: "50%",
                background: `radial-gradient(circle at 35% 30%, #FFFFFFcc 0%, ${t.accent}88 55%, ${t.accent}33 100%)`,
                boxShadow: `inset -1px -1px 2px ${t.ink}22`,
                opacity: reduce ? b.opacity : 0,
                animation: reduce
                  ? "none"
                  : `b03-bubble-rise ${b.duration}s linear ${b.delay}s infinite`,
                willChange: "transform, opacity",
                ["--b03-bub-op" as string]: String(b.opacity),
                ["--b03-bub-dx" as string]: `${b.drift}px`,
              } as React.CSSProperties}
            />
          ))}
        </div>

        {/* Static sparkle field — diamonds scattered (adult sophistication) */}
        <div
          aria-hidden
          style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 1 }}
        >
          {SPARKLE_POSITIONS.map((s, i) => (
            <span
              key={i}
              style={{
                position: "absolute",
                left: `${s.left}%`,
                top: `${s.top}%`,
                opacity: 0.45,
                color: t.accent,
              }}
            >
              <O.Diamond size={s.size} color={t.accent} />
            </span>
          ))}
        </div>

        {/* Left column — eyebrow, age callout, names, date/venue */}
        <div style={{ position: "relative", zIndex: 2 }}>
          <div
            className="b03-eyebrow"
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.42em",
              color: t.accent,
              marginBottom: 28,
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <span style={{ width: 28, height: 1, background: t.accent }} />
            {ageNumber
              ? `${ageNumber} YEARS · TURNS ${numToWord(parseInt(ageNumber, 10), "en").toUpperCase()}`
              : tt("variant.b03.hero.eyebrow")}
          </div>

          <h1
            className="b03-names"
            style={{
              fontFamily: t.font,
              fontStyle: "italic",
              fontSize: "clamp(64px, 9vw, 128px)",
              fontWeight: 400,
              lineHeight: 0.95,
              letterSpacing: "-0.02em",
              margin: 0,
              color: t.ink,
            }}
          >
            {cleanName}
          </h1>

          {/* Age callout — Bodoni Moda italic, big "30" with rose hairlines */}
          <div
            className="b03-age-callout"
            style={{
              marginTop: 28,
              display: "inline-flex",
              alignItems: "center",
              gap: 18,
              padding: "14px 26px",
              border: `1px solid ${t.accent}`,
              background: `${t.bg}`,
            }}
          >
            <O.Star size={12} color={t.accent} />
            <span
              style={{
                fontFamily: t.font,
                fontStyle: "italic",
                fontSize: 26,
                fontWeight: 400,
                color: t.accent,
                letterSpacing: "0.02em",
              }}
            >
              {ageNumber ? `${ageNumber} yosh` : tt("variant.b03.hero.age_label")}
            </span>
            <O.Star size={12} color={t.accent} />
          </div>

          <div
            style={{
              marginTop: 36,
              display: "flex",
              alignItems: "center",
              gap: 14,
              fontFamily: "var(--font-ui)",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.32em",
              color: `${t.ink}cc`,
            }}
          >
            <span>{t.date}</span>
            <span style={{ width: 24, height: 1, background: t.accent }} />
            <span>{t.venue.toUpperCase()}</span>
          </div>
        </div>

        {/* Right column — oval rose-tinted portrait */}
        <div className="b03-hero-photo" style={{ position: "relative", zIndex: 2 }}>
          <div
            style={{
              position: "relative",
              aspectRatio: "3 / 4",
              borderRadius: "50% / 44%",
              padding: 6,
              background: `linear-gradient(155deg, ${t.accent}, #E9CCB6)`,
              boxShadow: `0 18px 48px ${t.ink}22`,
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50% / 44%",
                overflow: "hidden",
                position: "relative",
                background: t.bg,
              }}
            >
              <GuestPhoto
                src={photos[0]}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                fallbackBg={t.bg}
                fallbackTone={t.accent}
              />
              {/* Soft rose wash for cohesion */}
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  inset: 0,
                  background: `linear-gradient(180deg, ${t.accent}10 0%, transparent 35%, ${t.accent}30 100%)`,
                  mixBlendMode: "multiply",
                  pointerEvents: "none",
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          COUNTDOWN
          ═══════════════════════════════════════════════════════════════ */}
      <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />

      {/* ═══════════════════════════════════════════════════════════════
          STORY — adult-tone Uzbek reflection
          ═══════════════════════════════════════════════════════════════ */}
      <StorySection
        t={t}
        eyebrow={tt("variant.b03.story.eyebrow")}
        title={tt("variant.b03.story.title")}
        body={tt("variant.b03.story.body")}
      />

      {/* ═══════════════════════════════════════════════════════════════
          UNIQUE: COCKTAIL MENU — three signature drinks
          ═══════════════════════════════════════════════════════════════ */}
      <section
        style={{
          padding: "120px 48px",
          borderTop: `1px solid ${t.ink}22`,
          background: `${t.accent}10`,
        }}
      >
        <div style={{ maxWidth: 880, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 56 }}>
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.42em",
                color: t.accent,
                marginBottom: 16,
              }}
            >
              {tt("variant.b03.cocktails.eyebrow")}
            </div>
            <h2
              style={{
                fontFamily: t.font,
                fontStyle: "italic",
                fontSize: "clamp(36px, 5vw, 56px)",
                fontWeight: 400,
                margin: 0,
              }}
            >
              {tt("variant.b03.cocktails.title")}
            </h2>
            <div
              style={{
                margin: "20px auto 0",
                width: 64,
                height: 1,
                background: t.accent,
                opacity: 0.7,
              }}
            />
          </div>

          <div
            className="b03-cocktails"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 24,
            }}
          >
            {COCKTAIL_KEYS.map((c, i) => (
              <Cocktail
                key={i}
                name={tt(c.nameKey)}
                recipe={tt(c.recipeKey)}
                accent={t.accent}
                ink={t.ink}
                bg={t.bg}
                font={t.font}
                onHover={() => play("pop")}
              />
            ))}
          </div>

          <div
            style={{
              marginTop: 48,
              textAlign: "center",
              fontFamily: t.font,
              fontStyle: "italic",
              fontSize: 18,
              color: `${t.ink}aa`,
            }}
          >
            {tt("variant.b03.cocktails.outro")}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          GALLERY
          ═══════════════════════════════════════════════════════════════ */}
      <GallerySection t={t} />

      {/* ═══════════════════════════════════════════════════════════════
          SCHEDULE
          ═══════════════════════════════════════════════════════════════ */}
      <ScheduleSection
        t={t}
        items={[
          ["19:00", tt("variant.b03.schedule.0.event")],
          ["19:30", tt("variant.b03.schedule.1.event")],
          ["20:30", tt("variant.b03.schedule.2.event")],
          ["22:00", tt("variant.b03.schedule.3.event")],
          ["23:00", tt("variant.b03.schedule.4.event")],
          ["02:00", tt("variant.b03.schedule.5.event")],
        ]}
      />

      {/* ═══════════════════════════════════════════════════════════════
          MAP
          ═══════════════════════════════════════════════════════════════ */}
      <MapSection
        t={t}
        isDark={isDark}
        addressLine={tt("variant.b03.map.address")}
      />

      {/* ═══════════════════════════════════════════════════════════════
          RSVP
          ═══════════════════════════════════════════════════════════════ */}
      <RsvpSection
        t={t}
        isDark={isDark}
        rsvp={ctx.rsvp.rsvp}
        setRsvp={wrappedSetRsvp}
        guests={ctx.rsvp.guests}
        setGuests={ctx.rsvp.setGuests}
        guestName={ctx.rsvp.guestName}
        setGuestName={ctx.rsvp.setGuestName}
        label={tt("variant.b03.rsvp.label")}
        slug={ctx.slug}
      />

      {/* ═══════════════════════════════════════════════════════════════
          GUESTBOOK
          ═══════════════════════════════════════════════════════════════ */}
      <GuestbookSection
        t={t}
        isDark={isDark}
        wishes={ctx.guestbook.wishes}
        setWishes={ctx.guestbook.setWishes}
        guestName={ctx.guestbook.guestName}
        setGuestName={ctx.guestbook.setGuestName}
        wish={ctx.guestbook.wish}
        setWish={ctx.guestbook.setWish}
        slug={ctx.slug}
      />

      {/* ═══════════════════════════════════════════════════════════════
          SHARE
          ═══════════════════════════════════════════════════════════════ */}
      <ShareSection t={t} />

      {/* ═══════════════════════════════════════════════════════════════
          FOOTER
          ═══════════════════════════════════════════════════════════════ */}
      <GuestFooter t={t} />
    </div>
  );
}

// ─── Single cocktail card ──────────────────────────────────────────────
function Cocktail({
  name,
  recipe,
  accent,
  ink,
  bg,
  font,
  onHover,
}: {
  name: string;
  recipe: string;
  accent: string;
  ink: string;
  bg: string;
  font: string;
  onHover: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <div
      className="b03-cocktail"
      onMouseEnter={() => {
        if (!hover) onHover();
        setHover(true);
      }}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        padding: "36px 28px 32px",
        background: bg,
        border: `1px solid ${accent}`,
        borderTop: `2px solid ${accent}`,
        textAlign: "center",
        transition: "transform 350ms ease, box-shadow 350ms ease",
        transform: hover ? "translateY(-4px)" : "translateY(0)",
        boxShadow: hover ? `4px 6px 0 -1px ${accent}55` : `2px 3px 0 -1px ${accent}22`,
      }}
    >
      {/* Tiny diamond accent at top-center */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          top: -7,
          left: "50%",
          transform: "translateX(-50%)",
          width: 12,
          height: 12,
          background: bg,
          display: "grid",
          placeItems: "center",
        }}
      >
        <svg width="10" height="10" viewBox="0 0 10 10">
          <path
            d="M5 0 L7 5 L5 10 L3 5 Z"
            fill={accent}
          />
        </svg>
      </div>
      <div
        style={{
          fontFamily: font,
          fontStyle: "italic",
          fontSize: 26,
          fontWeight: 400,
          color: ink,
          marginBottom: 12,
          letterSpacing: "-0.005em",
        }}
      >
        {name}
      </div>
      {/* Rose hairline */}
      <span
        aria-hidden
        style={{
          display: "block",
          width: 36,
          height: 1,
          background: accent,
          margin: "0 auto 14px",
          opacity: 0.7,
        }}
      />
      <div
        style={{
          fontFamily: font,
          fontStyle: "italic",
          fontSize: 14,
          color: `${ink}aa`,
          lineHeight: 1.5,
        }}
      >
        {recipe}
      </div>
    </div>
  );
}
